import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useScheduleMeeting, type ScheduleMeetingResponse } from "@/hooks/useCRM";
import type { CRMIndividual } from "@/types";

interface Props {
  open: boolean;
  onClose: () => void;
  companyId?: string;
  individuals: CRMIndividual[];
  defaultIndividualId?: string;
  opportunityId?: string;
  // Wave 2 (LinkedIn convert): callers can pre-fill the summary +
  // duration so e.g. "Schedule audit" sets up a 60-min Human Performance
  // Audit slot without the user retyping it.
  defaultSummary?: string;
  defaultDurationMin?: number;
  onScheduled?: (result: ScheduleMeetingResponse) => void;
}

// Round minutes up to the next quarter-hour for a nicer default than "now + 1h + a weird offset".
function defaultStartIso(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  return toLocalIsoForInput(d);
}

function plusMinutes(iso: string, minutes: number): string {
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() + minutes);
  return toLocalIsoForInput(d);
}

// <input type="datetime-local"> expects YYYY-MM-DDTHH:MM (no timezone, no seconds).
function toLocalIsoForInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Round-trip the local datetime-input value back into an ISO 8601 with timezone
// offset, so the backend stores it correctly and Google honours the right wall-clock.
function localInputToIsoWithTz(local: string): string {
  const d = new Date(local);
  return d.toISOString();
}

export default function ScheduleMeetingDialog({
  open,
  onClose,
  companyId,
  individuals,
  defaultIndividualId,
  opportunityId,
  defaultSummary,
  defaultDurationMin,
  onScheduled,
}: Props) {
  const [summary, setSummary] = useState("");
  const [startsAt, setStartsAt] = useState(defaultStartIso());
  const [durationMin, setDurationMin] = useState(30);
  const [agenda, setAgenda] = useState("");
  const [individualId, setIndividualId] = useState<string>(defaultIndividualId ?? "");
  const [extraAttendees, setExtraAttendees] = useState("");
  const [includeMeet, setIncludeMeet] = useState(true);
  const [sendInvites, setSendInvites] = useState(true);

  // Reset when re-opened so a previous draft doesn't leak.
  useEffect(() => {
    if (open) {
      setSummary(defaultSummary ?? "");
      setStartsAt(defaultStartIso());
      setDurationMin(defaultDurationMin ?? 30);
      setAgenda("");
      setIndividualId(defaultIndividualId ?? "");
      setExtraAttendees("");
      setIncludeMeet(true);
      setSendInvites(true);
    }
  }, [open, defaultIndividualId, defaultSummary, defaultDurationMin]);

  const mutation = useScheduleMeeting();

  const submit = async () => {
    if (!summary.trim() || !startsAt) return;
    const startsIso = localInputToIsoWithTz(startsAt);
    const endsIso = localInputToIsoWithTz(plusMinutes(startsAt, durationMin));
    const attendeeList = extraAttendees
      .split(/[,;\s]+/)
      .map((e) => e.trim())
      .filter((e) => e && e.includes("@"));
    try {
      const result = await mutation.mutateAsync({
        company_id: companyId,
        individual_id: individualId || null,
        opportunity_id: opportunityId,
        summary,
        starts_at: startsIso,
        ends_at: endsIso,
        agenda: agenda || null,
        attendee_emails: attendeeList.length > 0 ? attendeeList : undefined,
        include_google_meet: includeMeet,
        send_invites: sendInvites,
      });
      onScheduled?.(result);
      onClose();
    } catch {
      // Surface via mutation.error below — don't close the dialog.
    }
  };

  const selectedIndividual = individuals.find((i) => i.id === individualId);
  const errorDetail = mutation.error ? extractAxiosErrorDetail(mutation.error as unknown) : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Schedule a meeting</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errorDetail && (
            <Alert severity="error" variant="outlined">
              {errorDetail}
            </Alert>
          )}

          <TextField
            label="Summary"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="e.g. Q3 planning with Acme"
            required
            fullWidth
            autoFocus
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Starts at"
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
              sx={{ flex: 1 }}
            />
            <FormControl sx={{ minWidth: 130 }}>
              <InputLabel>Duration</InputLabel>
              <Select
                label="Duration"
                value={durationMin}
                onChange={(e) => setDurationMin(Number(e.target.value))}
              >
                <MenuItem value={15}>15 min</MenuItem>
                <MenuItem value={30}>30 min</MenuItem>
                <MenuItem value={45}>45 min</MenuItem>
                <MenuItem value={60}>1 hour</MenuItem>
                <MenuItem value={90}>1.5 hours</MenuItem>
                <MenuItem value={120}>2 hours</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {individuals.length > 0 && (
            <FormControl fullWidth>
              <InputLabel>With contact (auto-invites them)</InputLabel>
              <Select
                label="With contact (auto-invites them)"
                value={individualId}
                onChange={(e) => setIndividualId(String(e.target.value))}
              >
                <MenuItem value="">
                  <em>None — company-level meeting</em>
                </MenuItem>
                {individuals.map((ind) => (
                  <MenuItem key={ind.id} value={ind.id}>
                    {ind.first_name} {ind.last_name}
                    {ind.email ? ` — ${ind.email}` : " — (no email)"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {selectedIndividual && !selectedIndividual.email && (
            <Alert severity="warning" variant="outlined">
              {selectedIndividual.first_name} has no email — they won't get the invite. Add an email
              to their CRM record or list them in "Additional attendees" below.
            </Alert>
          )}

          <TextField
            label="Additional attendees (emails, comma or space separated)"
            value={extraAttendees}
            onChange={(e) => setExtraAttendees(e.target.value)}
            placeholder="alice@example.com, bob@example.com"
            fullWidth
          />

          <TextField
            label="Agenda / notes"
            value={agenda}
            onChange={(e) => setAgenda(e.target.value)}
            placeholder="What you want to cover (will appear in the calendar event description)"
            multiline
            minRows={3}
            fullWidth
          />

          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={
                <Switch checked={includeMeet} onChange={(e) => setIncludeMeet(e.target.checked)} />
              }
              label="Add Google Meet link"
            />
            <FormControlLabel
              control={
                <Switch checked={sendInvites} onChange={(e) => setSendInvites(e.target.checked)} />
              }
              label="Send invites to attendees"
            />
          </Stack>

          <Typography variant="caption" color="text.secondary">
            Creates a Google Calendar event on your primary calendar AND a meeting entry in the CRM,
            linked so you can log notes after the call.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={mutation.isPending || !summary.trim() || !startsAt}
        >
          {mutation.isPending ? "Scheduling…" : "Schedule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function extractAxiosErrorDetail(err: unknown): string | null {
  if (typeof err !== "object" || err === null) return null;
  const e = err as { response?: { data?: { detail?: string } }; message?: string };
  return e.response?.data?.detail ?? e.message ?? null;
}

// Re-export the type so the formatter shows a row in the upcoming list.
export function MeetingChip({ attendees, meet }: { attendees: string[]; meet?: boolean }) {
  return (
    <Stack direction="row" spacing={0.5}>
      {meet && <Chip label="Meet" size="small" color="primary" variant="outlined" />}
      {attendees.slice(0, 3).map((a) => (
        <Chip key={a} label={a} size="small" variant="outlined" />
      ))}
      {attendees.length > 3 && (
        <Chip label={`+${attendees.length - 3}`} size="small" variant="outlined" />
      )}
    </Stack>
  );
}
